import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";
import { CATEGORY_TAGS } from "@/lib/libraryConstants";

const MODE_IDS = ["define", "shape", "implement", "verify", "iterate"];

/**
 * Self-contained dialog for pulling blocks from the user's library into the
 * current project prompt. Parent provides `promptId` + `onPulled` callback.
 */
export function PullLibraryDialog({ open, onOpenChange, promptId, onPulled }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [pulling, setPulling] = useState(false);
  const [modeFilter, setModeFilter] = useState("all");
  const [tagFilters, setTagFilters] = useState(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    if (blocks.length === 0) {
      setLoading(true);
      API.get("/library", { params: { limit: 200 } })
        .then(({ data }) => setBlocks(data.items || []))
        .catch(() => toast.error("Failed to load library"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSelect = (idx) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };

  const handlePull = async () => {
    if (!promptId || selected.size === 0) return;
    setPulling(true);
    const ids = [...selected].map((idx) => blocks[idx]?.id).filter(Boolean);
    try {
      const { data } = await API.post(`/prompts/${promptId}/pull-blocks`, { library_block_ids: ids });
      toast.success(`Pulled ${data.added} blocks into playbook`);
      onPulled?.(data.prompt);
      handleClose(false);
    } catch (e) {
      toast.error("Pull failed");
    } finally {
      setPulling(false);
    }
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) { setModeFilter("all"); setTagFilters(new Set()); setSearch(""); setSelected(new Set()); }
  };

  const toggleTag = (tag) => {
    setTagFilters((prev) => {
      const n = new Set(prev);
      if (n.has(tag)) n.delete(tag); else n.add(tag);
      return n;
    });
    setSelected(new Set()); // invalidate selection since visible set changes
  };

  const filtered = blocks.filter((b) => {
    if (modeFilter !== "all" && b.mode !== modeFilter) return false;
    if (tagFilters.size > 0 && !(b.tags || []).some((t) => tagFilters.has(t))) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.title || "").toLowerCase().includes(q) || (b.content || "").toLowerCase().includes(q);
    }
    return true;
  });

  // Tag counts (global in this dialog — helps users see what's available)
  const tagCounts = blocks.reduce((acc, b) => {
    (b.tags || []).forEach((t) => { acc[t] = (acc[t] || 0) + 1; });
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogBody>
        <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Pull Playbook Blocks</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--pf-text-secondary)] -mt-1">Select blocks to copy into your project. Use Contextualize after pulling to fill {"{variables}"}.</p>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)] mx-auto mb-3" /><p className="text-sm text-[var(--pf-text-secondary)]">Loading library...</p></div>
        ) : blocks.length === 0 ? (
          <div className="p-8 text-center"><p className="text-sm text-[var(--pf-text-muted)]">No playbook blocks in your library. Install a starter pack or import prompts from the Library page.</p></div>
        ) : (
          <div data-testid="library-browse-list">
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="pull-mode-filter">
              {["all", ...MODE_IDS].map((m) => {
                const count = m === "all" ? blocks.length : blocks.filter((b) => b.mode === m).length;
                if (m !== "all" && count === 0) return null;
                return (
                  <button key={m} onClick={() => { setModeFilter(m); setSelected(new Set()); }} data-testid={`pull-mode-${m}`}
                    className={`text-[11px] px-2.5 py-1.5 font-medium transition-colors ${modeFilter === m ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                    {m === "all" ? "All" : m.charAt(0).toUpperCase() + m.slice(1)} <span className="ml-1 opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Category multi-select */}
            <div className="flex flex-wrap gap-1 mb-3" data-testid="pull-tag-filter">
              <button onClick={() => { setTagFilters(new Set()); setSelected(new Set()); }} data-testid="pull-tag-all"
                className={`text-[10px] px-1.5 py-0.5 font-medium transition-colors ${tagFilters.size === 0 ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                Any Category
              </button>
              {CATEGORY_TAGS.filter((t) => tagCounts[t]).map((t) => {
                const isActive = tagFilters.has(t);
                return (
                  <button key={t} onClick={() => toggleTag(t)} data-testid={`pull-tag-${t.toLowerCase().replace(/\s/g, "-")}`}
                    className={`text-[10px] px-1.5 py-0.5 font-medium transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                    {t} <span className="opacity-60 ml-0.5">{tagCounts[t]}</span>
                  </button>
                );
              })}
            </div>

            <div className="relative mb-3">
              <Sparkles className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--pf-text-muted)]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search blocks..." data-testid="pull-search"
                className="w-full h-8 text-xs pl-8 pr-3 border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] focus:ring-2 focus:ring-[var(--pf-accent)] focus:outline-none" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--pf-text-muted)]">{filtered.length} block{filtered.length !== 1 ? "s" : ""}</span>
              {selected.size > 0 && (
                <Button onClick={handlePull} disabled={pulling} data-testid="pull-blocks-button"
                  className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-8 px-3 text-xs font-medium">
                  {pulling ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Pulling...</> : <>Pull {selected.size} Blocks</>}
                </Button>
              )}
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {filtered.map((block, idx) => {
                const realIdx = blocks.indexOf(block);
                const isSelected = selected.has(realIdx);
                return (
                  <div key={block.id || idx} className={`border ${isSelected ? "border-[var(--pf-accent)] bg-[var(--pf-accent-subtle)]" : "border-[var(--pf-border)]"} p-3 cursor-pointer transition-colors hover:border-[var(--pf-accent)]`}
                    onClick={() => toggleSelect(realIdx)} data-testid={`lib-browse-${block.id || idx}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <input type="checkbox" checked={isSelected} readOnly className="w-3.5 h-3.5 rounded-none border-[var(--pf-border)] accent-[var(--pf-accent)] pointer-events-none shrink-0" />
                      <span className="text-xs font-semibold text-[var(--pf-text)] uppercase tracking-[0.1em] truncate">{block.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] shrink-0">{block.mode}</span>
                      {block.build_type && block.build_type !== "general" && <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-surface)] text-[var(--pf-text-muted)] shrink-0">{block.build_type}</span>}
                    </div>
                    <p className="text-xs text-[var(--pf-text)] font-mono whitespace-pre-wrap line-clamp-2 ml-5">{block.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
